import { Batch } from "@wingspanhq/wingspan-api-sdk";
import { useCallback } from "react";

import useSnackbar from "../../../hooks/useSnackbar";
import { downloadFileFromBlob } from "../../../utils/files";
import { selectorImportBatchUploadedFilename } from "../selectors/selectorImportBatchUploadedFilename";
import { getFlatfileMainSheetId } from "../utils/getFlatfileMainSheetId";
import { useFlatfileApiClient } from "./useFlatfileApiClient";

type Args = {
  batch: Batch;
  sheetSlug: string;
  /** Singular noun for what the batch imports, e.g. "Payable". */
  entityLabel: string;
};

function getSkippedRowsFilename(args: Args): string {
  const uploadedFilename = selectorImportBatchUploadedFilename({
    batch: args.batch,
    entityLabel: args.entityLabel
  });
  // The uploaded file can be a spreadsheet of any format, the skipped rows are
  // always a CSV.
  const uploadedFilenameWithoutExtension = uploadedFilename.replace(
    /\.[^.]+$/,
    ""
  );

  return `Skipped_rows_${
    uploadedFilenameWithoutExtension || args.entityLabel
  }.csv`;
}

/**
 * Downloads the spreadsheet rows that never made it into the batch, as a CSV.
 *
 * The rows stay in the Flatfile sheet, so they are fetched straight from
 * Flatfile by error state. `filter: "error"` rather than a search on the
 * listener's own uploadStatus column, because that column is only written to
 * rows we actually submitted: rows that failed validation are never sent, never
 * stamped, and a search misses them entirely. Error state covers both — the
 * listener enriches rejected rows with error messages, which is what makes them
 * invalid in Flatfile's eyes too.
 */
export function useDownloadSkippedRows(args: Args): () => Promise<void> {
  const { warning } = useSnackbar();
  const flatfileApiClient = useFlatfileApiClient(args.batch);
  const { batch, sheetSlug } = args;

  return useCallback(async () => {
    try {
      if (!flatfileApiClient) {
        throw new Error("No Flatfile access token on the batch");
      }

      const sheetId = await getFlatfileMainSheetId(
        flatfileApiClient,
        batch,
        sheetSlug
      );
      const csvStream = await flatfileApiClient.sheets.getRecordsAsCsv(
        sheetId,
        { filter: "error" }
      );
      // In the browser the Flatfile client resolves the download as a ReadableStream.
      const csvBlob = await new Response(
        csvStream as unknown as ReadableStream
      ).blob();

      downloadFileFromBlob(csvBlob, getSkippedRowsFilename(args));
    } catch (error) {
      console.error("Failed to download skipped rows", error);
      warning("Failed to download the skipped rows. Please try again.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatfileApiClient, batch, sheetSlug, warning]);
}
